import React, { useState, useEffect, RefObject, useRef } from 'react';
import ReactSVG from 'react-svg';
import { CircularProgress } from '@material-ui/core';
import {
  IconAnnouncement,
  IconAnnouncementPlay,
  IconAnnouncementPause,
  IconAnnouncementStop,
  IconAnnouncementStopDisabled,
} from '~/assets/svgIcons';
import { colors } from '~/layout';
import * as lib from '~/dependencies';
// eslint-disable-next-line import/no-cycle
import { PlayerStatusEnum } from '~/components/Announcements';
import {
  Row,
  Buttons,
  Button,
  ButtonIcon,
  Title,
  Progress,
  Info,
  Column,
  Description,
  AnnouncementName,
} from './styles';

type RenderItemProps = {
  item: { id: number; name: string; duration: string };
  handlePlay: (id: number) => void;
  handlePause: (id: number) => void;
  handleStop: () => void;
  announId: number;
  isLoading: boolean;
  status: string;
  audioEl: RefObject<HTMLMediaElement>;
};

export default function RenderItem({
  item,
  handlePlay,
  handlePause,
  handleStop,
  announId,
  isLoading,
  status,
  audioEl,
}: RenderItemProps) {
  // REF's
  // eslint-disable-next-line no-undef
  const intervalRef = useRef<NodeJS.Timer | null>(null);

  // STATES
  const [currTime, setCurrTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);

  // CONST's
  const isPlayingAnnoun = item.id === announId;

  // HOOK's
  const { t } = lib.useTranslation();

  // EFFECT's
  useEffect(() => {
    if (status === PlayerStatusEnum.Playing && isPlayingAnnoun) {
      intervalRef.current = setInterval(() => {
        if (audioEl.current) {
          setCurrTime(audioEl.current.currentTime);
          setDuration(audioEl.current.duration);
        }
      }, 300);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [status, isPlayingAnnoun, audioEl]);

  const handleLocalStop = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    handleStop();
    setCurrTime(0);
    setDuration(0);
  };

  const handlePlayPauseFunc = (id: number) => {
    if (status === PlayerStatusEnum.Playing && announId === id) {
      handlePause(id);
    } else {
      handlePlay(id);
    }
  };

  const getAudioProgress = (currentTime: number, durationTime: number) => {
    const progress = Math.floor((currentTime / durationTime) * 100);
    let value;

    if (Number.isNaN(progress) || progress === Infinity) {
      value = 14;
    } else if (progress > 95) {
      value = 100;
    } else if (progress < 18) {
      value = progress + 14;
    } else {
      value = progress + 5;
    }

    return value;
  };

  return (
    <Row>
      <Description>
        <ReactSVG src={IconAnnouncement} style={{ marginTop: 7 }} />
        <Column>
          <Info>
            <span style={{ opacity: 0.5 }}>{`${item.id}`}</span>
            <span>{`${item.duration}s`}</span>
          </Info>
          <AnnouncementName>{item.name}</AnnouncementName>
        </Column>
      </Description>
      <Buttons>
        <Button type="button" onClick={() => handlePlayPauseFunc(item.id)}>
          {isLoading && isPlayingAnnoun ? (
            <CircularProgress
              variant="indeterminate"
              disableShrink
              size={15}
              thickness={4}
              style={{ marginTop: 4, color: colors.lightBlue }}
              color="inherit"
            />
          ) : (
            <ButtonIcon>
              {(status === PlayerStatusEnum.Playing ||
                status === PlayerStatusEnum.Paused) &&
              isPlayingAnnoun ? (
                <Progress width={getAudioProgress(currTime, duration)} />
              ) : null}
              <Title>
                <ReactSVG
                  src={
                    status === PlayerStatusEnum.Playing && isPlayingAnnoun
                      ? IconAnnouncementPause
                      : IconAnnouncementPlay
                  }
                  style={{
                    width: 19,
                    height:
                      status === PlayerStatusEnum.Playing && isPlayingAnnoun
                        ? 14
                        : 19,
                  }}
                />
                <span
                  style={{
                    color: colors.black,
                    marginTop: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 50,
                  }}>
                  {status === PlayerStatusEnum.Playing && isPlayingAnnoun
                    ? t('components.announcements.renderItem.pause')
                    : t('components.announcements.renderItem.play')}
                </span>
              </Title>
            </ButtonIcon>
          )}
        </Button>
        <Button
          type="button"
          disabled={
            !isPlayingAnnoun ||
            (isPlayingAnnoun && status === PlayerStatusEnum.Stopped)
          }
          onClick={handleLocalStop}>
          <ButtonIcon>
            <Title>
              <ReactSVG
                src={
                  status !== PlayerStatusEnum.Stopped && isPlayingAnnoun
                    ? IconAnnouncementStop
                    : IconAnnouncementStopDisabled
                }
                style={{ marginLeft: 6, marginTop: 4 }}
              />
              <span
                style={{
                  marginTop: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 50,
                }}>
                {t('components.announcements.renderItem.stop')}
              </span>
            </Title>
          </ButtonIcon>
        </Button>
      </Buttons>
    </Row>
  );
}
